import React, { useMemo, useState, useEffect } from "react";
import { FaEye, FaDownload, FaPlus, FaTrash } from "react-icons/fa";
import Editor from "react-simple-wysiwyg";
import axiosInstance from "./api/axiosInstance";
import "../assets/css/QuoteGenerator.css";
import "../assets/css/ReceiptGenerator.css";
import { generateReceiptPdf } from "../utils/receiptPdf";
import { getCustomerSelectLabel, mapCustomerListItemFromApi } from "../utils/formatters";

const today = () => new Date().toISOString().split("T")[0];
const RECEIPT_PREFIX = "DGS";
const RECEIPT_COUNTER_KEY = "dgs_receipt_counter";

const getStartingReceiptNumber = () => {
  try {
    const stored = Number(localStorage.getItem(RECEIPT_COUNTER_KEY) || "0");
    const startFrom = Number.isFinite(stored) && stored > 0 ? stored : 1;
    localStorage.setItem(RECEIPT_COUNTER_KEY, String(startFrom));
    return `${RECEIPT_PREFIX}${startFrom}`;
  } catch (error) {
    return `${RECEIPT_PREFIX}1`;
  }
};

const customLine = () => ({
  id: Date.now() + Math.random(),
  serviceId: null,
  item: "",
  freeText: "",
  quantity: 1,
  rate: 0,
  advances: 0
});

function lineItemLabel(row) {
  const base = String(row.item || "").trim() || "Service";
  const note = String(row.freeText || "").trim();
  return note ? `${base} — ${note}` : base;
}

/** Receipt package options (no sub-services from catalog). */
const RECEIPT_PACKAGE_OPTIONS = [
  { id: "pkg-digital-marketing", name: "Digital Marketing", basePrice: 0 },
  { id: "pkg-graphic-design", name: "Graphic Design", basePrice: 0 },
  { id: "pkg-website-development", name: "Website Development", basePrice: 0 },
  { id: "pkg-web-application", name: "Web Application", basePrice: 0 },
  { id: "pkg-mobile-application", name: "Mobile Application", basePrice: 0 }
];

function ReceiptGenerator() {
  const [company] = useState({
    name: "DGS-Digi Growth Solution Pvt, Ltd.",
    address: "H.no:32-3-388, Anandhnagar Colony, Bhemaram, Hanamkonda, Warangal, Telangana, India - 506001",
    footer: "For any enquiry, reach out via email at info@digigrowth.net or call on +91 96761-87555"
  });
  const [invoice, setInvoice] = useState({
    invoiceNo: getStartingReceiptNumber(),
    invoiceDate: today(),
    notes: ""
  });
  const [billedTo, setBilledTo] = useState({
    companyName: "",
    name: "",
    email: "",
    phone: "",
    address: ""
  });

  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");

  useEffect(() => {
    const fetchCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const response = await axiosInstance.get("/clients");
        const raw = response.data;
        const arr = Array.isArray(raw) ? raw : raw?.data ?? [];
        const mapped = (arr || [])
          .map((item) => {
            const c = item.client || item;
            const base = mapCustomerListItemFromApi(item);
            if (!base) return null;
            return { ...c, ...base };
          })
          .filter(Boolean);
        setCustomers(mapped);
      } catch (err) {
        console.error("Error fetching customers:", err);
        setCustomers([]);
      } finally {
        setLoadingCustomers(false);
      }
    };
    fetchCustomers();
  }, []);

  const handleCustomerSelect = async (e) => {
    const id = e.target.value;
    setSelectedCustomerId(id);
    if (!id) {
      setBilledTo({ companyName: "", name: "", email: "", phone: "", address: "" });
      return;
    }
    const cFromList = customers.find((x) => String(x.id) === String(id));
    if (!cFromList) return;

    let customerObj = cFromList;
    let addressObj = cFromList.address || {};

    try {
      const res = await axiosInstance.get(`/client/${id}`);
      if (res.data) {
        customerObj = res.data.client || res.data;
        addressObj = res.data.address || res.data.Address || customerObj.address || {};
      }
    } catch (err) {
      console.error("Error fetching customer address:", err);
    }

    const addr = addressObj;
    const addressParts = [
      addr.Address_Line_1 || addr.address_line_1,
      addr.Address_Line_2 || addr.address_line_2,
      addr.City_Name || addr.city_name || addr.City,
      addr.State_Name || addr.state_name || addr.State,
      addr.Country_Name || addr.country_name || addr.Country,
      addr.Zip_Code || addr.zip_code || addr.Zip
    ].filter(Boolean);
    const address = addressParts.join(", ");
    const personName = `${customerObj.First_Name || ""} ${customerObj.Last_Name || ""}`.trim();
    const name = personName || customerObj.Customer_Name || customerObj.customer_name || customerObj.Company_Name || customerObj.company_name || "";
    setBilledTo({
      name,
      companyName: customerObj.Company_Name || customerObj.company_name || "",
      email: customerObj.Email || customerObj.email || customerObj.Company_Email || customerObj.company_email || "",
      phone: customerObj.Phone || customerObj.phone || customerObj.Company_Phone || customerObj.company_phone || "",
      address
    });
  };

  const [rows, setRows] = useState([]);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  /** Service lines table: show notes input after "Add notes" or when notes already saved */
  const [lineNotesExpanded, setLineNotesExpanded] = useState({});

  const totals = useMemo(() => {
    const invoicedAmount = rows.reduce((sum, r) => sum + Number(r.quantity || 0) * Number(r.rate || 0), 0);
    const advances = rows.reduce((sum, r) => sum + Number(r.advances || 0), 0);
    const dueAmount = Math.max(invoicedAmount - advances, 0);
    return { invoicedAmount, advances, dueAmount };
  }, [rows]);
  const pdfPayload = useMemo(
    () => ({
      company,
      invoice,
      billedTo,
      lineItems: rows.map((r) => {
        const qty = Number(r.quantity || 0);
        const rate = Number(r.rate || 0);
        const adv = Number(r.advances || 0);
        const amount = qty * rate;
        return {
          item: lineItemLabel(r),
          quantity: qty,
          rate,
          advances: adv,
          dueAmount: amount - adv
        };
      }),
      payment: totals
    }),
    [company, invoice, billedTo, rows, totals]
  );

  const isServiceSelected = (serviceId) =>
    rows.some((r) => r.serviceId != null && String(r.serviceId) === String(serviceId));

  const handleServiceToggle = (service, isChecked) => {
    if (isChecked) {
      setRows((prev) => [
        ...prev,
        {
          id: Date.now() + Math.random(),
          serviceId: service.id,
          item: service.name,
          freeText: "",
          quantity: 1,
          rate: Number(service.basePrice) || 0,
          advances: 0
        }
      ]);
    } else {
      setRows((prev) => prev.filter((r) => String(r.serviceId) !== String(service.id)));
    }
  };

  const updateRow = (id, key, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
  };

  const addCustomLine = () => setRows((prev) => [...prev, customLine()]);

  const removeRow = (id) => {
    setLineNotesExpanded((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const handlePreview = async () => {
    const doc = await generateReceiptPdf(pdfPayload);
    const url = URL.createObjectURL(doc.output("blob"));
    setPreviewUrl(url);
    setShowPreview(true);
  };

  const handleDownload = async () => {
    const doc = await generateReceiptPdf(pdfPayload);
    doc.save(`DIGIXLEADS_Receipt_${invoice.invoiceDate || today()}.pdf`);
    setInvoice((prev) => {
      const parsedCounter = Number(String(prev.invoiceNo || "").replace(RECEIPT_PREFIX, ""));
      const currentCounter = Number.isFinite(parsedCounter) && parsedCounter > 0 ? parsedCounter : 1;
      const nextCounter = currentCounter + 1;
      try {
        localStorage.setItem(RECEIPT_COUNTER_KEY, String(nextCounter));
      } catch (error) {
        // ignore localStorage write failures and continue with in-memory update
      }
      return { ...prev, invoiceNo: `${RECEIPT_PREFIX}${nextCounter}` };
    });
  };

  return (
    <div className="receipt-generator-page">
      <div className="receipt-generator-card">
        <div className="receipt-section">
          <h3>Receipt Details</h3>
          <div className="receipt-grid">
            <div className="receipt-field">
              <label className="receipt-field-label-primary">Receipt No</label>
              <input
                value={invoice.invoiceNo}
                onChange={(e) => setInvoice((p) => ({ ...p, invoiceNo: e.target.value }))}
              />
            </div>
            <div className="receipt-field">
              <label>Receipt Date</label>
              <input
                type="date"
                value={invoice.invoiceDate}
                onChange={(e) => setInvoice((p) => ({ ...p, invoiceDate: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div className="receipt-section">
          <h3>Customer details</h3>
          <div className="receipt-grid receipt-grid-customer-details">
            <div className="receipt-field">
              <label className="receipt-field-label-primary">Customer name</label>
              <select
                value={selectedCustomerId}
                onChange={handleCustomerSelect}
                disabled={loadingCustomers}
              >
                <option value="">
                  {loadingCustomers ? "Loading clients..." : "Select client"}
                </option>
                {!loadingCustomers &&
                  customers.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {getCustomerSelectLabel(c)}
                    </option>
                  ))}
              </select>
            </div>
            <div className="receipt-field">
              <label>Customer number</label>
              <input
                type="tel"
                value={billedTo.phone}
                onChange={(e) => setBilledTo((p) => ({ ...p, phone: e.target.value }))}
              />
            </div>
            <div className="receipt-field">
              <label>Email</label>
              <input
                type="email"
                value={billedTo.email}
                onChange={(e) => setBilledTo((p) => ({ ...p, email: e.target.value }))}
              />
            </div>
            <div className="receipt-field">
              <label>Company name</label>
              <input
                value={billedTo.companyName}
                onChange={(e) => setBilledTo((p) => ({ ...p, companyName: e.target.value }))}
              />
            </div>
            <div className="receipt-field receipt-field-full">
              <label>Address</label>
              <input
                value={billedTo.address}
                onChange={(e) => setBilledTo((p) => ({ ...p, address: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div className="receipt-section receipt-services-quote-style">
          <h3 className="quote-packages-section-title">Select Services</h3>
          <div className="receipt-services-single-card">
            <div className="receipt-packages-grid">
              {RECEIPT_PACKAGE_OPTIONS.map((pkg) => {
                const selected = isServiceSelected(pkg.id);
                return (
                  <label key={pkg.id} className="receipt-package-option">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={(e) => handleServiceToggle(pkg, e.target.checked)}
                    />
                    <span className="receipt-package-option-label">{pkg.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="receipt-section">
          <h3>Service</h3>
          <div className="receipt-items-wrap">
            <div className="receipt-items-head receipt-items-head-services">
              <span>Service</span>
              <span>Notes</span>
              <span>Invoice amount</span>
              <span>Due amount</span>
              <span>Paid amount</span>
              <span>Action</span>
            </div>
            {rows.length === 0
              ? null
              : rows.map((r) => {
                const lineTotal = Number(r.quantity || 0) * Number(r.rate || 0);
                const paid = Number(r.advances || 0);
                const lineDue = Math.max(lineTotal - paid, 0);
                const hasLineNotes = Boolean(String(r.freeText || "").trim());
                const showLineNotesInput = lineNotesExpanded[r.id] || hasLineNotes;
                return (
                  <div className="receipt-item-row receipt-item-row-services" key={r.id}>
                    {r.serviceId != null ? (
                      <span className="receipt-service-name-cell">{r.item}</span>
                    ) : (
                      <input
                        value={r.item}
                        onChange={(e) => updateRow(r.id, "item", e.target.value)}
                      />
                    )}
                    {showLineNotesInput ? (
                      <input
                        type="text"
                        value={r.freeText}
                        onChange={(e) => updateRow(r.id, "freeText", e.target.value)}
                      />
                    ) : (
                      <button
                        type="button"
                        className="receipt-add-notes-inline-btn"
                        onClick={() => setLineNotesExpanded((prev) => ({ ...prev, [r.id]: true }))}
                      >
                        Add notes
                      </button>
                    )}
                    <input
                      type="number"
                      min={paid}
                      value={lineTotal}
                      onChange={(e) => {
                        const v = Number(e.target.value || 0);
                        const minTotal = Number(r.advances || 0);
                        const safeTotal = Math.max(v, minTotal);
                        setRows((prev) =>
                          prev.map((row) =>
                            row.id === r.id ? { ...row, quantity: 1, rate: safeTotal } : row
                          )
                        );
                      }}
                    />
                    <input
                      type="text"
                      readOnly
                      tabIndex={-1}
                      className="receipt-due-input-readonly"
                      value={`₹${lineDue.toLocaleString("en-IN")}`}
                      aria-label="Due amount"
                    />
                    <input
                      type="number"
                      min="0"
                      value={paid}
                      onChange={(e) => {
                        const nextPaid = Number(e.target.value || 0);
                        const maxPaid = Number(r.quantity || 0) * Number(r.rate || 0);
                        updateRow(r.id, "advances", Math.min(nextPaid, maxPaid));
                      }}
                    />
                    <button type="button" className="receipt-icon-btn" onClick={() => removeRow(r.id)}>
                      <FaTrash />
                    </button>
                  </div>
                );
              })}
          </div>
          <button type="button" className="receipt-add-row" onClick={addCustomLine}>
            <FaPlus /> Add custom line
          </button>
        </div>

        <div className="receipt-summary">
          <div className="receipt-summary-box">
            <strong>Receipt amount</strong>
            <span>₹{totals.invoicedAmount.toLocaleString("en-IN")}</span>
          </div>
          <div className="receipt-summary-box">
            <strong>Advances</strong>
            <span>₹{totals.advances.toLocaleString("en-IN")}</span>
          </div>
          <div className="receipt-summary-box receipt-summary-due">
            <strong>Due amount</strong>
            <span className="receipt-due">₹{totals.dueAmount.toLocaleString("en-IN")}</span>
          </div>
        </div>

        <div className="receipt-section">
          <h3>Notes</h3>
          <div className="receipt-field">
            <Editor
              id="receipt-notes"
              name="receipt-notes"
              value={invoice.notes}
              onChange={(e) => setInvoice((p) => ({ ...p, notes: e.target.value }))}
              containerProps={{ style: { minHeight: "240px" } }}
            />
          </div>
        </div>

        <div className="receipt-actions">
          <button type="button" className="btn-quote-preview" onClick={handlePreview}>
            <FaEye /> Preview
          </button>
          <button type="button" className="btn-quote-download" onClick={handleDownload}>
            <FaDownload /> PDF
          </button>
        </div>
      </div>

      {showPreview && previewUrl ? (
        <div className="quote-preview-overlay quote-pdf-preview-overlay" onClick={() => setShowPreview(false)}>
          <div className="quote-preview-popup" onClick={(e) => e.stopPropagation()}>
            <div className="quote-preview-header">
              <h3>Receipt PDF Preview</h3>
              <button type="button" onClick={() => setShowPreview(false)}>
                Close
              </button>
            </div>
            <iframe
              src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0&pagemode=none&view=FitH&zoom=page-width`}
              className="quote-preview-iframe"
              title="Receipt Preview"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ReceiptGenerator;
